// src/components/subscriptions/SubscriptionInvoice.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../../hooks/useLogo';
import './SubscriptionInvoice.css';

const SubscriptionInvoice = ({ subscription, onCancel, onRetryPayment, onRequestRefund, loading }) => {
    const navigate = useNavigate();

    const fmt = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
    };

    const fmtLong = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const fmtMoney = (amount, currency) => {
        const n = Number(amount || 0);
        return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const cur = subscription.currency || 'USD';
    const curSymbol = cur === 'PKR' ? '₨' : '$';

    const subtotal       = Number(subscription.subtotal || subscription.amount || 0);
    const setupFee       = Number(subscription.setup_fee || 0);
    const discountAmt    = Number(subscription.discount_amount || 0);
    const discountPct    = Number(subscription.discount_percentage || 0);
    const taxPct         = Number(subscription.tax_percentage || 0);
    const taxAmt         = Number(subscription.tax_amount || 0);
    const totalAmount    = Number(subscription.amount || 0);
    const staffCount     = Number(subscription.staff_count || 0);
    const unitPrice      = Number(subscription.unit_price || subscription.price_per_user || 0);
    // Derived from the reconciled subtotal (tied to the real charged amount/discount),
    // not recomputed from the pricing tier's current unit price - so this always
    // reconciles exactly with the "Subtotal (before discount)" footer row below,
    // even if the tier's pricing has changed since this subscription was purchased.
    const recurringAmount = subtotal - setupFee;
    const isFree         = staffCount < 5;
    const durationMonths = Number(subscription.duration_months || 1);
    const invoiceNum     = `INV-${subscription.id?.toString().padStart(6, '0') || '000001'}`;
    const isExtensionActive = Boolean(subscription.is_extension_active);
    const extensionDays = Number(subscription.extension_days || 0);

    const getStatusInfo = (status) => {
        switch (status) {
            case 'active':     return { color: '#10b981', text: 'ACTIVE' };
            case 'pending':    return { color: '#f59e0b', text: 'PENDING PAYMENT' };
            case 'confirming': return { color: '#3b82f6', text: 'CONFIRMING' };
            case 'expired':    return { color: '#ef4444', text: 'EXPIRED' };
            case 'cancelled':  return { color: '#6b7280', text: 'CANCELLED' };
            default:           return { color: '#6b7280', text: (status || '').toUpperCase() };
        }
    };
    const statusInfo = getStatusInfo(subscription.status);

    const handlePrint = () => {
        const printable = document.querySelector('.subscription-overview .inv-wrap') || document.querySelector('.inv-wrap');
        if (!printable) {
            window.print();
            return;
        }

        const printWindow = window.open('', '_blank', 'width=1280,height=860');
        if (!printWindow) {
            window.print();
            return;
        }

        // Resolve stylesheet hrefs to absolute URLs - a root-relative href like
        // "/static/css/main.css" can fail to load in a window opened with
        // window.open('', ...) depending on how the browser resolves about:blank's
        // base URL, so make sure every href is unambiguous before copying it over.
        const styleNodes = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
            .map((node) => {
                if (node.tagName === 'LINK') {
                    const clone = node.cloneNode(true);
                    clone.setAttribute('href', new URL(node.getAttribute('href'), window.location.href).href);
                    return clone.outerHTML;
                }
                return node.outerHTML;
            })
            .join('\n');

        printWindow.document.write(`
            <!doctype html>
            <html>
            <head>
                <meta charset="utf-8" />
                <title>Subscription Invoice</title>
                <base href="${window.location.origin}/" />
                ${styleNodes}
                <style>
                    body { margin: 0; padding: 16px; background: #fff; }
                    .no-print { display: none !important; }
                </style>
            </head>
            <body>
                ${printable.outerHTML}
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();

        // Wait for the copied stylesheets to actually finish loading instead of
        // guessing with a fixed delay - on a slow connection (especially the
        // external Font Awesome CDN link) a fixed timeout can fire before the
        // styles are applied, which is what makes the print/PDF look unstyled.
        let printed = false;
        const triggerPrint = () => {
            if (printed) return;
            printed = true;
            printWindow.print();
            printWindow.close();
        };
        printWindow.addEventListener('load', () => setTimeout(triggerPrint, 150));
        setTimeout(triggerPrint, 2000); // safety net if 'load' never fires
    };

    return (
        <div className="inv-wrap">

            {/* ── Header ───────────────────────────────────────────── */}
            <div className="inv-header">
                <div className="inv-company">
                    <Logo size="large" className="inv-logo" />
                    <div className="inv-company-details">
                        <span>Attendance Management System</span>
                        <span>support@ehaazri.com</span>
                    </div>
                </div>
                <div className="inv-meta-block">
                    <table className="inv-meta-table">
                        <tbody>
                            <tr><th>Invoice #</th><td>{invoiceNum}</td></tr>
                            <tr><th>Date</th><td>{fmtLong(subscription.created_at || subscription.start_date)}</td></tr>
                            <tr>
                                <th>Status</th>
                                <td>
                                    <span className="inv-status-badge" style={{ background: statusInfo.color }}>
                                        {statusInfo.text}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <hr className="inv-divider" />

            {/* ── Bill-to / Period ─────────────────────────────────── */}
            <div className="inv-billing-row">
                <div className="inv-bill-section">
                    <div className="inv-section-label">Bill To</div>
                    <div className="inv-org-name">{subscription.organization_name || 'Your Organisation'}</div>
                    <div className="inv-org-id">Organisation ID: {subscription.organization_id}</div>
                </div>
                <div className="inv-bill-section">
                    <div className="inv-section-label">Subscription Period</div>
                    {isExtensionActive && (
                        <div className="inv-period-row" style={{ color: '#0ea5e9', fontWeight: 700 }}>
                            <span>Extension:</span> Active (+{extensionDays} day{extensionDays === 1 ? '' : 's'})
                        </div>
                    )}
                    {durationMonths > 1 && (
                        <div className="inv-period-row"><span>Duration:</span> {durationMonths} Months</div>
                    )}
                    <div className="inv-period-row"><span>Start:</span> {fmtLong(subscription.start_date)}</div>
                    <div className="inv-period-row">
                        <span>End:</span> {subscription.end_date ? fmtLong(subscription.end_date) : '∞ No Expiry'}
                    </div>
                </div>
            </div>

            <hr className="inv-divider" />

            {/* ── Charges Table ────────────────────────────────────── */}
            <h2 className="inv-section-heading">Charges &amp; Credits</h2>

            <div className="inv-table-wrap">
            <table className="inv-table">
                <thead>
                    <tr>
                        <th>Description</th>
                        <th className="inv-th-center">Term</th>
                        <th className="inv-th-right">Unit Price</th>
                        <th className="inv-th-right">Qty</th>
                        <th className="inv-th-right">Amount</th>
                        {discountAmt > 0 && <th className="inv-th-right">Discount ({discountPct}%)</th>}
                        {taxAmt > 0    && <th className="inv-th-right">Tax ({taxPct}%)</th>}
                        <th className="inv-th-right">Total</th>
                    </tr>
                </thead>
                <tbody>
                    {/* Main subscription line */}
                    <tr>
                        <td>
                            <strong>{isFree ? 'Free Tier Subscription' : 'eHAAZRI Attendance Software — Subscription'}</strong>
                            <br /><small>Attendance tracking for up to {staffCount} staff members</small>
                        </td>
                        <td className="inv-td-center">
                            {durationMonths === 1 ? '1 Month' : `${durationMonths} Months`}
                        </td>
                        <td className="inv-td-right">
                            {cur} {fmtMoney(unitPrice)}
                        </td>
                        <td className="inv-td-right">{staffCount}</td>
                        <td className="inv-td-right">
                            {cur} {fmtMoney(recurringAmount)}
                        </td>
                        {discountAmt > 0 && (
                            <td className="inv-td-right inv-discount">
                                − {cur} {fmtMoney(discountAmt)}
                            </td>
                        )}
                        {taxAmt > 0 && (
                            <td className="inv-td-right inv-tax">
                                {cur} {fmtMoney(taxAmt)}
                            </td>
                        )}
                        <td className="inv-td-right">
                            {cur} {fmtMoney(totalAmount - setupFee)}
                        </td>
                    </tr>

                    {/* Setup fee line */}
                    {setupFee > 0 && (
                        <tr>
                            <td>
                                <strong>One-Time Setup Fee</strong>
                                <br /><small>Initial configuration &amp; onboarding</small>
                            </td>
                            <td className="inv-td-center">Once</td>
                            <td className="inv-td-right">{cur} {fmtMoney(setupFee)}</td>
                            <td className="inv-td-right">1</td>
                            <td className="inv-td-right">{cur} {fmtMoney(setupFee)}</td>
                            {discountAmt > 0 && <td className="inv-td-right">—</td>}
                            {taxAmt > 0    && <td className="inv-td-right">—</td>}
                            <td className="inv-td-right">{cur} {fmtMoney(setupFee)}</td>
                        </tr>
                    )}
                </tbody>

                {/* Totals footer */}
                <tfoot>
                    <tr className="inv-subtotal-row">
                        <td colSpan={4 + (discountAmt > 0 ? 1 : 0) + (taxAmt > 0 ? 1 : 0)} className="inv-td-label">
                            Subtotal (before discount)
                        </td>
                        <td className="inv-td-right">{cur} {fmtMoney(subtotal)}</td>
                        <td></td>
                    </tr>

                    {discountAmt > 0 && (
                        <tr className="inv-discount-row">
                            <td colSpan={4 + (discountAmt > 0 ? 1 : 0) + (taxAmt > 0 ? 1 : 0)} className="inv-td-label">
                                Approved Discount ({discountPct}%)
                            </td>
                            <td className="inv-td-right inv-discount">− {cur} {fmtMoney(discountAmt)}</td>
                            <td></td>
                        </tr>
                    )}

                    {taxAmt > 0 && (
                        <tr className="inv-tax-row">
                            <td colSpan={4 + (discountAmt > 0 ? 1 : 0) + (taxAmt > 0 ? 1 : 0)} className="inv-td-label">
                                Tax ({taxPct}%)
                            </td>
                            <td className="inv-td-right inv-tax">+ {cur} {fmtMoney(taxAmt)}</td>
                            <td></td>
                        </tr>
                    )}

                    <tr className="inv-total-row">
                        <td colSpan={4 + (discountAmt > 0 ? 1 : 0) + (taxAmt > 0 ? 1 : 0)} className="inv-td-label">
                            <strong>Total Invoice Amount</strong>
                        </td>
                        <td className="inv-td-right"><strong>{cur} {fmtMoney(totalAmount)}</strong></td>
                        <td></td>
                    </tr>
                </tfoot>
            </table>
            </div>

            {/* ── Payments Table ───────────────────────────────────── */}
            {subscription.payment_status && (
                <>
                    <h2 className="inv-section-heading">Payments</h2>
                    <div className="inv-table-wrap">
                    <table className="inv-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Reference / Order #</th>
                                <th>Payment Method</th>
                                <th className="inv-th-right">Amount Paid</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{fmt(subscription.created_at)}</td>
                                <td>{subscription.payment_reference || invoiceNum}</td>
                                <td>{subscription.payment_method || subscription.payment_gateway || '—'}</td>
                                <td className="inv-td-right">
                                    {subscription.payment_status === 'completed'
                                        ? <><strong>{cur} {fmtMoney(totalAmount)}</strong></>
                                        : <span className="inv-status-badge" style={{ background: statusInfo.color, fontSize: '0.75rem' }}>
                                            {subscription.payment_status?.toUpperCase()}
                                          </span>
                                    }
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    </div>
                </>
            )}

            {/* ── Status Actions ───────────────────────────────────── */}
            <div className="inv-actions-panel">
                {subscription.status === 'pending' && (
                    <div className="inv-alert warning">
                        <i className="fas fa-exclamation-triangle"></i>
                        <div>
                            <strong>Payment Pending</strong>
                            <p>Complete payment to activate this subscription, or cancel if no longer needed.</p>
                        </div>
                        <div className="inv-alert-btns">
                            <button className="btn btn-primary" onClick={onRetryPayment} disabled={loading}>
                                <i className="fas fa-credit-card"></i> {loading ? 'Processing…' : 'Complete Payment'}
                            </button>
                            <button className="btn btn-danger" onClick={onCancel} disabled={loading}>
                                <i className="fas fa-times"></i> Cancel
                            </button>
                        </div>
                    </div>
                )}

                {subscription.status === 'confirming' && (
                    <div className="inv-alert info">
                        <i className="fas fa-hourglass-half"></i>
                        <div>
                            <strong>Payment Confirmation In Progress</strong>
                            <p>We have received your payment and are currently verifying it. Your subscription will activate within 24–48 hours.</p>
                        </div>
                    </div>
                )}

                {subscription.status === 'active' && (
                    <div className="inv-alert success">
                        <i className="fas fa-check-circle"></i>
                        <div>
                            <strong>Subscription Active</strong>
                            <p>Your subscription is active and will {subscription.end_date ? `expire on ${fmtLong(subscription.end_date)}` : 'never expire (Free Tier)'}.</p>
                            {isExtensionActive && (
                                <p><strong>Extension Notice:</strong> Your organization is currently on an approved extension period.</p>
                            )}
                        </div>
                        <div className="inv-alert-btns">
                            <button className="btn btn-secondary" onClick={() => navigate('/subscriptions/purchase')}>
                                <i className="fas fa-arrow-up"></i> Upgrade
                            </button>
                            {subscription.duration_months > 1
                                && subscription.refund_status !== 'requested'
                                && subscription.refund_status !== 'approved'
                                && onRequestRefund && (
                                <button className="btn btn-warning" onClick={onRequestRefund} disabled={loading}>
                                    <i className="fas fa-undo"></i> Request Refund
                                </button>
                            )}
                            {subscription.refund_status === 'requested' && (
                                <span className="inv-refund-badge"><i className="fas fa-clock"></i> Refund Requested</span>
                            )}
                            {subscription.refund_status === 'approved' && (
                                <span className="inv-refund-badge approved"><i className="fas fa-check"></i> Refund Approved</span>
                            )}
                        </div>
                    </div>
                )}

                {subscription.status === 'expired' && (
                    <div className="inv-alert error">
                        <i className="fas fa-exclamation-circle"></i>
                        <div>
                            <strong>Subscription Expired</strong>
                            <p>Your subscription has expired. Renew now to continue using Haazri services.</p>
                        </div>
                        <div className="inv-alert-btns">
                            <button className="btn btn-primary" onClick={() => navigate('/subscriptions/purchase')}>
                                <i className="fas fa-redo"></i> Renew
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Notes ───────────────────────────────────────────── */}
            <div className="inv-notes">
                <h4>Please Note:</h4>
                <ol>
                    <li>This is a computer-generated invoice and does not require a signature.</li>
                    <li>For billing inquiries please contact <strong>support@ehaazri.com</strong></li>
                    {isFree && <li>Free Tier subscriptions never expire and require no payment.</li>}
                    {subscription.status === 'pending' && (
                        <li>Payment must be completed within 24 hours or the subscription will be automatically cancelled.</li>
                    )}
                    {subscription.status === 'confirming' && (
                        <li>Payment verification is in progress. You will be notified once your subscription is activated.</li>
                    )}
                    {taxAmt > 0 && (
                        <li>Taxes charged reflect your organisation's jurisdiction as configured by your administrator.</li>
                    )}
                    {discountAmt > 0 && (
                        <li>An approved discount of {discountPct}% has been applied to this invoice.</li>
                    )}
                </ol>
            </div>

            {/* ── Print / Download ────────────────────────────────── */}
            <div className="inv-print-bar no-print">
                <button className="btn btn-ghost" onClick={handlePrint}>
                    <i className="fas fa-print"></i> Print Invoice
                </button>
                <button className="btn btn-ghost" onClick={handlePrint}>
                    <i className="fas fa-download"></i> Download PDF
                </button>
            </div>
        </div>
    );
};

export default SubscriptionInvoice;
